// Szikra (DESIGN.md "Diagramok" 3): a trend in a table cell or a tile.
// Eight measurements, no axis, a smooth line with a soft gradient under it, a
// ringed dot on the last one, and the threshold as a dashed line when there
// is one. The line is the series colour; the dot carries the state.
//
// The line is SVG stretched to the box (non-scaling stroke); the dot and the
// threshold are laid over it in HTML, so the dot stays round and the dashes
// stay even whatever the cell's width.

import { useId } from "react";
import { cn } from "@/lib/utils";
import type { Tone } from "@/components/emc/status";
import { TONE_FILL } from "@/components/emc/tone";
import { smoothPath } from "@/components/emc/charts/smooth";
import type { Point } from "@/components/emc/charts/smooth";

const POINTS = 8;

/** The line's colour for a tone, when no series colour is given. */
const LINE: Record<Tone, string> = {
  running: "var(--chart-1)",
  scheduled: "var(--emc-indigo)",
  good: "var(--emc-success)",
  warn: "var(--emc-warning)",
  danger: "var(--destructive)",
  muted: "var(--muted-foreground)",
};

interface Props {
  /** Oldest first; the last POINTS are drawn. `null` is a missing day. */
  values: (number | null)[];
  threshold?: number;
  /** The end dot's state (and the line's colour when `color` is not given). */
  tone?: Tone;
  /** The series colour, a token: `var(--chart-3)` for the HU pool. */
  color?: string;
  /** What the numbers are, for a screen reader: "bounce-arány, 8 nap". */
  ariaLabel: string;
  format?: (value: number) => string;
  /** Size comes from the class; the default is h-6 w-20. */
  className?: string;
}

// The drawing box, in the SVG's own units: 0–100 both ways, with a margin
// so the line never touches the edge the dot sits on.
const X_FROM = 4;
const X_SPAN = 92;
const Y_FROM = 12;
const Y_SPAN = 76;

export function Sparkline({
  values,
  threshold,
  tone = "running",
  color,
  ariaLabel,
  format = (value) => value.toLocaleString("hu-HU"),
  className,
}: Props) {
  const gradientId = useId();
  const recent = values.slice(-POINTS);
  const known = recent.filter((value): value is number => value !== null);
  const box = cn("relative inline-block h-6 w-20 align-middle text-muted-foreground", className);

  if (known.length === 0) {
    return (
      <span className={box} role="img" aria-label={`${ariaLabel}: nincs adat`}>
        <span className="absolute inset-0 flex items-center">—</span>
      </span>
    );
  }

  const scale = threshold === undefined ? known : [...known, threshold];
  const low = Math.min(...scale);
  const high = Math.max(...scale);
  const range = high - low || 1;
  const step = recent.length > 1 ? X_SPAN / (recent.length - 1) : 0;
  const xOf = (index: number) => X_FROM + index * step;
  const yOf = (value: number) => Y_FROM + Y_SPAN - ((value - low) / range) * Y_SPAN;

  const segments: Point[][] = [];
  let current: Point[] = [];
  recent.forEach((value, index) => {
    if (value === null) {
      if (current.length > 0) segments.push(current);
      current = [];
      return;
    }
    current.push([xOf(index), yOf(value)]);
  });
  if (current.length > 0) segments.push(current);
  const stroke = color ?? LINE[tone];

  const lastIndex = recent.length - 1 - [...recent].reverse().findIndex((value) => value !== null);
  const last = recent[lastIndex] ?? known[known.length - 1] ?? 0;
  const spoken = recent.map((value) => (value === null ? "—" : format(value))).join(", ");

  return (
    <span className={box} role="img" aria-label={`${ariaLabel}: ${spoken}`} title={spoken}>
      <svg
        viewBox="0 0 100 100"
        preserveAspectRatio="none"
        className="absolute inset-0 size-full overflow-visible"
        aria-hidden="true"
      >
        <defs>
          <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" style={{ stopColor: stroke, stopOpacity: 0.28 }} />
            <stop offset="1" style={{ stopColor: stroke, stopOpacity: 0 }} />
          </linearGradient>
        </defs>
        {segments.map((points) => {
          const first = points[0] as Point;
          const last = points[points.length - 1] as Point;
          const line = smoothPath(points);
          return (
            <g key={line}>
              {points.length > 1 && (
                <path d={`${line}L${last[0]},100L${first[0]},100Z`} fill={`url(#${gradientId})`} stroke="none" />
              )}
              <path
                d={line}
                fill="none"
                style={{ stroke }}
                strokeWidth={1.75}
                strokeLinejoin="round"
                strokeLinecap="round"
                vectorEffect="non-scaling-stroke"
              />
            </g>
          );
        })}
      </svg>
      {threshold !== undefined && (
        <span
          aria-hidden="true"
          className="absolute inset-x-0 border-t border-dashed border-muted-foreground/60"
          style={{ top: `${yOf(threshold)}%` }}
        />
      )}
      <span
        aria-hidden="true"
        className={cn(
          "absolute size-2 -translate-x-1/2 -translate-y-1/2 rounded-full outline-2 outline-card",
          TONE_FILL[tone],
        )}
        style={{ left: `${xOf(lastIndex)}%`, top: `${yOf(last)}%` }}
      />
    </span>
  );
}
